import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import React from "react";
import { Link } from "react-router-dom";
import useApi from "../../hooks/useGaneshApi";
import { Sample_T } from "../../types/Sample";
import { getDateValue, renderDateCell, renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";

import { useQuery } from "@tanstack/react-query";
import { useError } from "../../contexts/ErrorContext";
import DisplayDatasetSchemaModalButton from "../Schema/DisplayDatasetSchemaModalButton";

interface CustomerSamplesTableProps {
  customerId?: string;
}

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "updated_at", sort: "desc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: {
    columnVisibilityModel: {
      sales_request_date: false,
      ac_completed_date: false,
      requested_delivery_date: false,
      completed_date: false,
      fulfillment_date: false,
      canceled_date: false,
    },
  },
  density: "compact",
};

const columns_def: GridColDef[] = [
  {
    field: "id",
    headerName: "Sample ID",
    width: 100,
    renderCell: (params: GridRenderCellParams<Number>) => (
      <Link to={`/sample/${params.value}`}>{params.value}</Link>
    ),
  },
  {
    field: "schema_config",
    headerName: "Schema Config",
    width: 150,
    renderCell: (params: GridRenderCellParams) => (
      <DisplayDatasetSchemaModalButton
        datasetId={params.row.id}
        datasetName={params.row.name}
        datasetType="sample"
      />
    ),
  },
  {
    field: "name",
    headerName: "Sample Name",
    width: 200,
    renderCell: (params: GridRenderCellParams) => (
      <Link to={`/sample/${params.row.id}`}>{params.value}</Link>
    ),
  },

  {
    field: "regions",
    headerName: "Regions",
    width: 120,
    renderCell: (params: GridRenderCellParams<string[]>) => (
      <span>{params.value ? params.value.join(", ") : ""}</span>
    ),
  },
  {
    field: "geos",
    headerName: "Geos",
    width: 120,
    renderCell: (params: GridRenderCellParams<string[]>) => (
      <span>{params.value ? params.value.join(", ") : ""}</span>
    ),
  },
  {
    field: "updated_at",
    headerName: "Updated At",
    width: 150,
    type: "date",
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "sales_to_customer",
    headerName: "Sales To Customer Days",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "ops_to_customer",
    headerName: "Ops To Customer Days",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "place_export_row_count",
    headerName: "Row Count Before Post Export Scrubbing",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "status",
    headerName: "Status",
    width: 150,
  },
  {
    field: "watermark_ratio",
    headerName: "Watermark Ratio",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "scrubbed_coordinates_ratio",
    headerName: "Scrubbed Coordinates Ratio",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "confidence_score_fader_level",
    headerName: "Confidence Score Fader Level",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "sales_request_date",
    headerName: "Sales Request Date",
    width: 150,
    type: "date",
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "ac_completed_date",
    headerName: "AC Completed Date",
    width: 150,
    type: "date",
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "requested_delivery_date",
    headerName: "Requested Delivery Date",
    width: 150,
    type: "date",
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "completed_date",
    headerName: "Completed Date",
    width: 150,
    type: "date",
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "fulfillment_date",
    headerName: "Fulfillment Date",
    width: 150,
    type: "date",
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "canceled_date",
    headerName: "Canceled Date",
    width: 150,
    type: "date",
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
];

const CustomerSamplesTable: React.FC<CustomerSamplesTableProps> = ({
  customerId,
}) => {
  const { showError } = useError();
  const { apiRequest } = useApi();

  const {
    data: customerSampleData = [],
    isLoading,
    isError,
    error,
  } = useQuery<Sample_T[], Error>({
    queryKey: ["samples", customerId],
    queryFn: async () => {
      const response = await apiRequest<Sample_T[]>(
        `/v1/customers/${customerId}/samples`
      );
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data || [];
    },
    enabled: !!customerId,
  });

  if (isError) {
    showError(
      `Failed to fetch samples for customer ${customerId}: ${error.message}`
    );
  }

  return (
    <GaneshDataGrid
      loading={isLoading}
      data={customerSampleData}
      columns={columns_def}
      gridId="customerSamplesGrid"
      defaultGridState={defaultGridState}
    />
  );
};

export default CustomerSamplesTable;
